import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const TYPE_BADGE_CLASS = {
  pdf: "bg-rose-50 text-rose-700 border-rose-200",
  video: "bg-violet-50 text-violet-700 border-violet-200",
  article: "bg-sky-50 text-sky-700 border-sky-200",
  link: "bg-emerald-50 text-emerald-700 border-emerald-200",
  other: "bg-amber-50 text-amber-700 border-amber-200",
};

// One colour identity per type, used for the fallback cover (when there is no
// real thumbnail) and the card's top accent bar. Bolder than the badge tint so
// the catalogue reads at a glance: pdf=red, video=violet, article=blue,
// link=green, other=amber/yellow.
const TYPE_THEME = {
  pdf: { cover: "from-rose-500 to-rose-700", accent: "bg-rose-500", tile: "text-rose-600" },
  video: { cover: "from-violet-500 to-violet-800", accent: "bg-violet-500", tile: "text-violet-600" },
  article: { cover: "from-sky-500 to-blue-700", accent: "bg-sky-500", tile: "text-sky-600" },
  link: { cover: "from-emerald-500 to-emerald-700", accent: "bg-emerald-500", tile: "text-emerald-600" },
  other: { cover: "from-amber-400 to-amber-600", accent: "bg-amber-400", tile: "text-amber-600" },
};
export const typeTheme = (type) => TYPE_THEME[type] || TYPE_THEME.other;

// The site's favicon (Google's resolver) for an external URL — a small "logo"
// shown on the fallback cover so an article/link without an og:image still
// reads as that site. Degrades to nothing via onError.
export const faviconSrc = (url) => {
  try {
    const host = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=128`;
  } catch {
    return null;
  }
};

// A simple per-type glyph for the fallback cover tile.
function TypeGlyph({ type, className }) {
  const paths = {
    pdf: "M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z",
    article:
      "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2zM7 8h10M7 12h10M7 16h6",
    link: "M13.8 10.2a4 4 0 00-5.6 0l-2.5 2.5a4 4 0 005.6 5.6l1-1m1.9-7.1a4 4 0 015.6 0 4 4 0 010 5.6l-2.5 2.5a4 4 0 01-5.6 0",
    other: "M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z",
  };
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={paths[type] || paths.other} />
    </svg>
  );
}

export function ResourceTypeBadge({ type }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wide ${
        TYPE_BADGE_CLASS[type] || "bg-slate-100 text-slate-600 border-slate-200"
      }`}
    >
      {t(`materials.type.${type}`, { defaultValue: type })}
    </span>
  );
}

// Extract the 11-char video id from any common YouTube URL: watch (v= in any
// position, e.g. ?si=…&v=ID or ?list=…&v=ID), youtu.be, /shorts/, /live/,
// /embed/, and the privacy-friendly youtube-nocookie.com host.
const YT_RE =
  /(?:youtube(?:-nocookie)?\.com\/(?:embed|shorts|live)\/|youtu\.be\/|youtube(?:-nocookie)?\.com\/watch\?(?:[^#]*&)?v=)([\w-]{11})/;
export const youTubeThumb = (url) => {
  const m = (url || "").match(YT_RE);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};
// The bare 11-char video id (or null). The YouTube IFrame Player API needs a
// videoId — not the /embed/ URL — to construct a YT.Player, which is how the
// detail page tracks/resumes playback position.
export const youTubeId = (url) => {
  const m = (url || "").match(YT_RE);
  return m ? m[1] : null;
};
// A direct video file (mp4/webm/ogg/mov, ignoring any ?query/#hash) -> the src to
// drop into a native <video>; null otherwise so the player falls back to an embed.
const VIDEO_FILE_RE = /\.(mp4|webm|ogg|mov)(\?|#|$)/i;
export const directVideoSrc = (src) => (VIDEO_FILE_RE.test(src || "") ? src : null);
// An image file -> the src to drop into an <img> (renders inline anywhere, no
// third party). null otherwise.
const IMAGE_FILE_RE = /\.(png|jpe?g|gif)(\?|#|$)/i;
export const imageSrc = (src) => (IMAGE_FILE_RE.test(src || "") ? src : null);
// An Office document (Word/PowerPoint/Excel) -> the Microsoft Office Online
// embed URL. NOTE: that viewer fetches the file server-side, so it only renders
// when `src` is PUBLICLY reachable (a deployed URL) — not on localhost. null for
// non-Office files.
const OFFICE_FILE_RE = /\.(docx?|pptx?|xlsx?)(\?|#|$)/i;
export const officeEmbedSrc = (src) =>
  OFFICE_FILE_RE.test(src || "")
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(src)}`
    : null;
export const prettyHost = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};
// Best-effort inline PDF source. An uploaded file (served by our own backend)
// embeds directly; an external PDF link is routed through Google's viewer because
// many hosts forbid cross-origin framing (X-Frame-Options) — so a direct iframe
// would just show "refused to connect". The "Open original" button is always the
// reliable fallback.
export const pdfFrameSrc = (material) => {
  if (material?.file_url) return material.file_url;
  if (material?.url)
    return `https://docs.google.com/gview?url=${encodeURIComponent(material.url)}&embedded=true`;
  return null;
};

// Resolve the best real thumbnail for a material's card, or null when none and
// the coloured fallback cover should render instead:
//   1. preview_image_url — backend-fetched og:image (articles/links/etc.)
//   2. uploaded image file — shown directly
//   3. video — YouTube thumbnail from the URL
export function previewImageSrc(material) {
  if (material.preview_image_url) return material.preview_image_url;
  if (material.resource_type === "video") return youTubeThumb(material.url) || null;
  if (imageSrc(material.file_url)) return material.file_url;
  return null;
}

// The visual at the top of each card. When a real thumbnail exists it fills the
// 16:9 frame (with a play overlay for video); otherwise a type-coloured cover
// shows the site favicon + domain (external URLs) or a type glyph. Shared by the
// admin manage page (Materials.js) and the teacher-facing cards.
export function MaterialPreview({ material }) {
  const type = material.resource_type;
  const theme = typeTheme(type);
  const [imgError, setImgError] = useState(false);
  const [iconError, setIconError] = useState(false);

  const thumb = imgError ? null : previewImageSrc(material);
  const isVideo = type === "video";

  if (thumb) {
    return (
      <div className="relative aspect-video w-full overflow-hidden bg-slate-900">
        <img
          src={thumb}
          alt=""
          loading="lazy"
          onError={() => setImgError(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {isVideo && (
          <>
            <div className="absolute inset-0 bg-slate-900/25 group-hover:bg-slate-900/10 transition" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-105 transition">
                <svg className="w-5 h-5 text-slate-900 ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </div>
          </>
        )}
      </div>
    );
  }

  // Fallback cover: type-coloured gradient with a white tile. For external URLs
  // the tile shows the site favicon (+ domain); otherwise a type glyph.
  const host = prettyHost(material.url);
  const favicon = !iconError ? faviconSrc(material.url) : null;
  return (
    <div
      className={`relative aspect-video w-full overflow-hidden bg-gradient-to-br ${theme.cover} flex flex-col items-center justify-center gap-2`}
    >
      <span className="w-14 h-14 rounded-2xl bg-white/95 shadow-lg flex items-center justify-center group-hover:scale-105 transition">
        {favicon ? (
          <img
            src={favicon}
            alt=""
            loading="lazy"
            onError={() => setIconError(true)}
            className="w-7 h-7 object-contain"
          />
        ) : (
          <TypeGlyph type={type} className={`w-7 h-7 ${theme.tile}`} />
        )}
      </span>
      {host && (
        <span className="text-[11px] font-semibold text-white/90 max-w-[12rem] truncate px-2">
          {host}
        </span>
      )}
    </div>
  );
}

// Where a material card's preview/title should link to: prefer the uploaded
// file, fall back to the external URL. Shared so the admin and teacher cards
// resolve the link target identically.
export const materialHref = (material) =>
  material.file_url || material.url || null;

// Shared card chrome for the admin (Materials.js) and teacher
// (MaterialTeacherCard.js) surfaces: renders the preview header (type badge +
// the href-wrapped MaterialPreview) and opens the card body with the title +
// description block, then slots each card's own body content via `children`.
// Composing this in both keeps the top half of the two cards visually
// identical while each surface keeps its distinct footer + body extras.
//
// `headerExtra` lets a card slot an extra absolutely-positioned overlay into
// the preview header (e.g. the teacher card's "read" badge).
//
// `linkTo` switches the preview + title from an external <a href> (admin card,
// default) to an in-app react-router <Link to={linkTo}> (teacher card → the
// detail page). When set it takes precedence over the external href.
export function MaterialCardMedia({
  material,
  headerExtra = null,
  linkTo = null,
  children,
}) {
  const href = materialHref(material);
  const theme = typeTheme(material.resource_type);
  const preview = <MaterialPreview material={material} />;

  // Internal-route link wins over the external href when linkTo is provided.
  const wrapPreview = (node, extraClass = "") => {
    if (linkTo) {
      return (
        <Link to={linkTo} aria-label={material.title} className={extraClass}>
          {node}
        </Link>
      );
    }
    if (href) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          aria-label={material.title}
          className={extraClass}
        >
          {node}
        </a>
      );
    }
    return node;
  };

  return (
    <>
      <div className={`h-1 ${theme.accent}`} />
      <div className="relative">
        <span className="absolute top-2.5 left-2.5 z-10">
          <ResourceTypeBadge type={material.resource_type} />
        </span>
        {headerExtra}
        {wrapPreview(preview, "block")}
      </div>

      <div className="flex-1 flex flex-col p-4 gap-2">
        <h4 className="font-bold text-slate-900 text-sm leading-snug line-clamp-2">
          {linkTo || href
            ? wrapPreview(material.title, "hover:text-indigo-600 transition")
            : material.title}
        </h4>
        {material.description && (
          <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
            {material.description}
          </p>
        )}

        {children}
      </div>
    </>
  );
}

// Loading placeholder matching a material card's silhouette (preview block +
// title/desc lines + footer bar). Shared by the admin grid and the teacher
// library so both pages flash an identical skeleton.
export function MaterialCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-pulse">
      <div className="aspect-video bg-slate-100" />
      <div className="p-4 space-y-3">
        <div className="h-3.5 rounded bg-slate-200 w-3/4" />
        <div className="h-2.5 rounded bg-slate-100 w-full" />
        <div className="h-2.5 rounded bg-slate-100 w-2/3" />
      </div>
      <div className="h-9 border-t border-slate-100 bg-slate-50/60" />
    </div>
  );
}
